import { useState } from "react";
import { FolderInput, Mail, MailOpen, Star, StarOff, Trash2, X } from "lucide-react";
import { Button } from "../../components/Button";
import { Select } from "../../components/Select";
import { mailbox } from "../../api/mailbox";
import type { BatchAction } from "../../api/mailbox";
import type { MailBatchResult, MailFolder } from "../../api/types";

// 勾選多封之後的批次列(規格 §2.6):刪除、標已讀 / 未讀、加 / 去星號、搬到資料夾。
// 一個動作 = **一個** `POST /batch`(後端依資料夾分組、一條連線做完)。結果交給頁面:
// `partial: true` 要誠實講幾封成功、幾封沒做成,並重抓列表。
//
// 垃圾桶裡的「刪除」是**從雲端永久刪除**(後端 purge):與單封刪除一樣先問,同一句話。

/** 從雲端永久刪除前問的那一句(單封與批次共用)。 */
export const PURGE_PROMPT = "這封信會從雲端永久刪除(系統備份還留著,照樣看得到)。確定刪除?";

export interface MailBatchBarProps {
  ids: number[];
  folders: MailFolder[];
  /** 目前所在的資料夾(「搬到」的選項不列它)。全部資料夾時是 null。 */
  currentFolder: string | null;
  /** 勾的信裡有垃圾桶裡的:「刪除」會從雲端永久刪除,要先問。 */
  purgeOnDelete: boolean;
  disabled: boolean;
  onDone: (result: MailBatchResult, action: BatchAction) => void;
  onError: (err: unknown) => void;
  onClear: () => void;
}

export function MailBatchBar({
  ids,
  folders,
  currentFolder,
  purgeOnDelete,
  disabled,
  onDone,
  onError,
  onClear,
}: MailBatchBarProps) {
  const [busy, setBusy] = useState(false);
  const [dest, setDest] = useState("");
  if (ids.length === 0) return null;

  const run = async (action: BatchAction, folder?: string) => {
    if (busy) return;
    if (action === "trash" && purgeOnDelete && !window.confirm(PURGE_PROMPT)) return;
    setBusy(true);
    try {
      const result = await mailbox.batch(ids, action, folder);
      onDone(result, action);
      setDest("");
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const targets = folders.filter((f) => f.name !== currentFolder);
  const off = disabled || busy;

  return (
    <section
      aria-label="批次動作"
      className="flex flex-col gap-2 rounded-[var(--radius)] border border-[var(--accent)] bg-[var(--accent-soft)] p-2"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-[var(--text)]">已選 {ids.length} 封</span>
        <Button size="sm" variant="ghost" icon={<X aria-hidden="true" size={14} />} onClick={onClear}>
          取消選取
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" disabled={off} icon={<Trash2 aria-hidden="true" size={14} />} onClick={() => void run("trash")}>
          刪除
        </Button>
        <Button size="sm" disabled={off} icon={<MailOpen aria-hidden="true" size={14} />} onClick={() => void run("seen")}>
          標已讀
        </Button>
        <Button size="sm" disabled={off} icon={<Mail aria-hidden="true" size={14} />} onClick={() => void run("unseen")}>
          標未讀
        </Button>
        <Button size="sm" disabled={off} icon={<Star aria-hidden="true" size={14} />} onClick={() => void run("flag")}>
          加星號
        </Button>
        <Button size="sm" disabled={off} icon={<StarOff aria-hidden="true" size={14} />} onClick={() => void run("unflag")}>
          去星號
        </Button>
      </div>
      <div className="flex flex-wrap items-end gap-1.5">
        <Select
          label="搬到資料夾"
          value={dest}
          disabled={off}
          onChange={(e) => setDest(e.target.value)}
          options={[{ value: "", label: "選一個資料夾…" }, ...targets.map((f) => ({ value: f.name, label: f.label }))]}
        />
        <Button
          size="sm"
          disabled={off || !dest}
          icon={<FolderInput aria-hidden="true" size={14} />}
          onClick={() => void run("move", dest)}
        >
          搬移
        </Button>
      </div>
    </section>
  );
}

/** 批次結果給人看的那一句。全部成功回 null(頁面用提示就好)。 */
export function batchSummary(result: MailBatchResult): string | null {
  const done = result.done?.length ?? 0;
  const failed = result.failed?.length ?? 0;
  const skipped = result.skipped?.length ?? 0;
  if (!result.partial && failed === 0 && skipped === 0) return null;
  const parts = [`${done} 封完成`];
  if (failed > 0) parts.push(`${failed} 封沒有做成(雲端連線中途斷了,請稍後再試一次)`);
  if (skipped > 0) parts.push(`${skipped} 封在雲端已經刪除,略過`);
  return parts.join(";");
}
